import React from "react";

import AddMediaDialog from "@src/features/app/dialogs/addMedia/AddMediaDialog";
import { MediaFragment } from "@src/fragments.generated";
import { Button } from "@src/lib/ui/Button";
import PhotoOutlineIcon from "@src/lib/icons/18px/photo-outline";
import FileDropZone from "@src/lib/ui/FileDropZone";
import FileDropZoneSurface from "@src/lib/ui/FileDropZoneSurface";
import { gql } from "@apollo/client";
import { useToast } from "@src/lib/hooks/useToast";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import { useMessageBlastDialogCreateMediaMutation } from "@src/features/messaging/MessageBlastFileDropZone.generated";
import { AddMediaResources_TrainingResourceFragment } from "@src/features/app/dialogs/addMedia/Resources.generated";
import { SentMessage_MessageBlastMediaFragment } from "./SentMessage.generated";

type Props = {
  messageBlastMedias: Set<SentMessage_MessageBlastMediaFragment>;
  setMessageBlastMedias: (
    newSet: Set<SentMessage_MessageBlastMediaFragment>,
  ) => void;
};

const nextIndex = (
  medias: Set<SentMessage_MessageBlastMediaFragment>,
): number => {
  const indices = Array.from(medias, (m) => m.index);
  return indices.length === 0 ? 0 : Math.max(...indices) + 1;
};

const MessageBlastFileDropZone: React.FC<Props> = (props) => {
  const { addErrorToast } = useToast();
  const [isFileUploading, setIsFileUploading] = React.useState(false);
  const [createMedia] = useMessageBlastDialogCreateMediaMutation({
    onCompleted(data) {
      if (data.createMedia.success && data.createMedia.media) {
        const media = data.createMedia.media;
        const messageBlastMediaObj = {
          id: media.id,
          media,
          pinned: false,
          index: nextIndex(props.messageBlastMedias),
        };
        props.setMessageBlastMedias(
          new Set([...props.messageBlastMedias, messageBlastMediaObj]),
        );
      } else {
        addErrorToast({
          ...data.createMedia.error,
          callsite: "add_media_dialog",
        });
      }
    },
    onError: (error) => {
      addErrorToast({
        ...error,
        callsite: "add_media_dialog",
      });
    },
  });
  const handleOnCreateMedia = React.useCallback(
    (url: string) => {
      createMedia({
        variables: {
          input: {
            mediaUrls: emptyTranslationSet({
              en: url,
              translationsDisabled: true,
            }),
          },
        },
      });
    },
    [createMedia],
  );
  return (
    <FileDropZone
      disabled={false}
      setMediaUrl={handleOnCreateMedia}
      isFileUploading={isFileUploading}
      setIsFileUploading={setIsFileUploading}
    >
      <FileDropZoneSurface
        className="mb-4 py-4"
        icon={null}
        title={
          <>
            Drop files here or{" "}
            <span className="underline underline-offset-2">upload file</span>
          </>
        }
        action={
          <AddMediaDialog
            title="Add media"
            onMediaChange={(media?: MediaFragment) => {
              if (media) {
                const messageBlastMediaObj = {
                  id: media.id,
                  media,
                  pinned: false,
                  index: nextIndex(props.messageBlastMedias),
                };
                props.setMessageBlastMedias(
                  new Set([...props.messageBlastMedias, messageBlastMediaObj]),
                );
              }
            }}
            onResourceChange={(
              trainingResource?: AddMediaResources_TrainingResourceFragment,
            ) => {
              const newMedia = trainingResource?.publishedVersion?.media;

              if (
                trainingResource?.libraryItem &&
                trainingResource?.publishedVersion
              ) {
                const newMessageBlastMedia = {
                  id: Date.now(),
                  pinned: false,
                  index: nextIndex(props.messageBlastMedias),
                  libraryItem: {
                    id: trainingResource?.libraryItem?.id,
                    type: trainingResource?.libraryItem?.type,
                    name: trainingResource?.libraryItem?.name,
                    trainingResource: {
                      id: trainingResource?.id,
                      publishedVersion: {
                        id: trainingResource?.publishedVersion?.id,
                        type: trainingResource?.publishedVersion?.type,
                        media: trainingResource?.publishedVersion?.media,
                        richTextDocument:
                          trainingResource?.publishedVersion?.richTextDocument,
                      },
                    },
                  },
                };
                props.setMessageBlastMedias(
                  new Set([...props.messageBlastMedias, newMessageBlastMedia]),
                );
              } else if (newMedia) {
                const newMessageBlastMedia = {
                  id: newMedia.id,
                  media: newMedia,
                  pinned: false,
                  index: nextIndex(props.messageBlastMedias),
                };
                props.setMessageBlastMedias(
                  new Set([...props.messageBlastMedias, newMessageBlastMedia]),
                );
              }
            }}
            trigger={
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <PhotoOutlineIcon />
                Add media
              </Button>
            }
          />
        }
      />
    </FileDropZone>
  );
};

gql`
  mutation MessageBlastDialogCreateMedia($input: MediaInput!) {
    createMedia(input: $input) {
      success
      error {
        code
      }
      media {
        ...Media
      }
    }
  }
`;

export default MessageBlastFileDropZone;
